/**
 * A tenant's key-pickup details for ONE booking (COL-533).
 *
 * V2's room page needs what V1's `CheckInCheckOut` fetched for itself, so that
 * the check-in information can live in Infos & Documents instead of a second,
 * duplicate layout. Three deliberate differences from V1's query:
 *
 * 1. IT TAKES A BOOKING, rather than re-deciding which booking is active.
 *    V1 selects every confirmed booking for the user and takes the first. The
 *    V2 page has already resolved the booking it is rendering, and a hook that
 *    picked a different one would show a tenant the keys to another room.
 *
 * 2. NO `status = 'confirmed'` FILTER. V1 only ever ran on confirmed
 *    bookings; the V2 room page also renders `active`, `notice_given` and
 *    `moved_out` tenancies, and copying that filter would leave the block
 *    blank for everybody already living there. Scoping by id is both narrower
 *    and more honest — RLS still restricts the row to its own tenant.
 *
 * 3. ITS OWN CACHE KEY. V1 carries a hard-won comment about this: it first
 *    shared `["tenant-approved-bookings", user.id]` with Dashboard's leaner
 *    query, Dashboard's result won the cache, and key pickup silently rendered
 *    blank. A distinct key avoids inheriting that trap.
 *
 * The room → apartment → property inheritance is NOT re-derived here: it goes
 * through the same `resolveEffectiveKeyPickup` the admin surfaces use, so a
 * tenant reads the pickup location an admin configured rather than a second
 * opinion about it.
 */
import { useQuery } from "@tanstack/react-query";

import { resolveEffectiveKeyPickup } from "@/hooks/v2/useProperties";
import { supabase } from "@/lib/supabase";
import type { EffectiveKeyPickup } from "@/types/property";

export type TenantKeyNest = {
  /** Per-room link, falling back to the property's for single-key buildings. */
  accessUrl: string | null;
  lockerNumber: string | null;
  pickupCode: string | null;
  accessCode: string | null;
  openingHoursWeekdays: string | null;
  openingHoursSaturday: string | null;
  statusLabel: string | null;
};

const EMPTY: TenantKeyNest = {
  accessUrl: null,
  lockerNumber: null,
  pickupCode: null,
  accessCode: null,
  openingHoursWeekdays: null,
  openingHoursSaturday: null,
  statusLabel: null,
};

export type TenantKeyPickup = {
  keynest: TenantKeyNest;
  pickup: EffectiveKeyPickup | null;
  /** True when there is anything at all to show. */
  hasAny: boolean;
  isPending: boolean;
  isError: boolean;
};

export function useTenantKeyPickup(booking: any): TenantKeyPickup {
  // bookings.id is a bigint column, so the filter must be numeric.
  const bookingId = booking?.id != null ? Number(booking.id) : null;

  const { data, isPending, isError } = useQuery({
    queryKey: ["tenant-key-pickup", bookingId],
    enabled: bookingId != null && Number.isFinite(bookingId),
    queryFn: async () => {
      const { data, error } = await supabase
        .from("bookings")
        .select(
          `
          id,
          rooms (
            id,
            keynest_access_url,
            keynest_partner_shop_name,
            keynest_partner_shop_address,
            keynest_locker_number,
            keynest_pickup_code,
            keynest_access_code,
            keynest_opening_hours_weekdays,
            keynest_opening_hours_saturday,
            keynest_status_label,
            pickup_method,
            pickup_instructions,
            apartments (
              keynest_pickup_location,
              keynest_pickup_address,
              pickup_method,
              pickup_instructions
            ),
            properties (
              keynest_pickup_location,
              keynest_pickup_address,
              keynest_access_url,
              pickup_method,
              pickup_instructions
            )
          )
        `
        )
        .eq("id", bookingId)
        .maybeSingle();
      if (error) throw error;
      return data ?? null;
    },
  });

  const room: any = (data as any)?.rooms ?? null;

  const keynest: TenantKeyNest = room
    ? {
        accessUrl:
          room.keynest_access_url ??
          room.properties?.keynest_access_url ??
          null,
        lockerNumber: room.keynest_locker_number ?? null,
        pickupCode: room.keynest_pickup_code ?? null,
        accessCode: room.keynest_access_code ?? null,
        openingHoursWeekdays: room.keynest_opening_hours_weekdays ?? null,
        openingHoursSaturday: room.keynest_opening_hours_saturday ?? null,
        statusLabel: room.keynest_status_label ?? null,
      }
    : EMPTY;

  const pickup = room
    ? resolveEffectiveKeyPickup({
        room: room as any,
        apartment: (room.apartments ?? null) as any,
        property: (room.properties ?? null) as any,
      })
    : null;

  /*
    "Is there anything to show" — the same test V1 applies. Note it does NOT
    include the codes: a locker number or pickup code with no location and no
    link is not a pickup a tenant can act on, and showing a bare code would
    invite them to turn up nowhere in particular.
  */
  const hasAny =
    !!keynest.accessUrl ||
    !!pickup?.location ||
    !!pickup?.address ||
    !!pickup?.instructions;

  return { keynest, pickup, hasAny, isPending, isError };
}
